import React, { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface FileDropzoneProps {
  selectedFile: File | null;
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
  accept?: Record<string, string[]>;
  hasError?: boolean;
  errorMessage?: string;
  hint?: string;
  maxSizeMB?: number;
  fileTooLargeMessage?: string;
  invalidTypeMessage?: string;
  disabled?: boolean;
  displayName?: string;
  /** A client page's own texts: the dashboard's exist in French and English only. */
  labels?: { choose: string; none: string; remove: string };
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  selectedFile,
  onFileSelected,
  onFileRemoved,
  accept,
  hasError = false,
  errorMessage,
  hint,
  maxSizeMB,
  fileTooLargeMessage,
  invalidTypeMessage,
  disabled = false,
  displayName,
  labels,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [internalError, setInternalError] = useState<string | null>(null);

  const acceptString = accept
    ? [...Object.keys(accept), ...Object.values(accept).flat()].join(",")
    : undefined;

  const acceptedExtensions = accept
    ? Object.values(accept)
        .flat()
        .map((ext) => ext.toLowerCase())
    : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setInternalError(null);

    if (!file) return;

    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      setInternalError(
        fileTooLargeMessage || (t("common.fileTooLarge") as string)
      );
      e.target.value = "";
      return;
    }

    if (acceptedExtensions) {
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      if (!acceptedExtensions.includes(fileExtension)) {
        setInternalError(
          invalidTypeMessage || (t("common.invalidFileType") as string)
        );
        e.target.value = "";
        return;
      }
    }

    onFileSelected(file);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setInternalError(null);
    onFileRemoved();
    if (inputRef.current) inputRef.current.value = "";
  };

  const showError = hasError || !!internalError;
  const shownName = selectedFile?.name || displayName;
  const isPickable = !shownName && !disabled;

  const rowClassName = `flex items-center justify-between w-full px-4 py-2.5 border rounded-lg transition-colors ${
    disabled
      ? "bg-gray-100 cursor-not-allowed opacity-50"
      : shownName
      ? "bg-gray-50 cursor-default"
      : "bg-white hover:bg-gray-50 cursor-pointer"
  } ${
    showError ? "border-red-500" : "border-gray-300"
  } focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent`;

  const rowContent = (
    <>
      <span
        className={`text-sm truncate flex-1 mr-2 ${
          shownName ? "text-gray-800 font-medium" : "text-gray-500"
        }`}
      >
        {shownName || (labels?.none ?? (t("common.noFileChosen") as string))}
      </span>

      {shownName && !disabled ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}
          className="inline-flex items-center px-3 py-1.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
        >
          {labels?.remove ?? (t("common.remove") as string)}
        </button>
      ) : (
        <span className="inline-flex items-center px-4 py-2 bg-gray-800 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors flex-shrink-0">
          {labels?.choose ?? (t("common.chooseFile") as string)}
        </span>
      )}
    </>
  );

  return (
    <div>
      <input
        type="file"
        id={inputId}
        ref={inputRef}
        accept={acceptString}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
      />

      {isPickable ? (
        <label htmlFor={inputId} className={rowClassName}>
          {rowContent}
        </label>
      ) : (
        <div className={rowClassName}>{rowContent}</div>
      )}

      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}

      {internalError && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {internalError}
        </p>
      )}

      {hasError && !internalError && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {errorMessage || (t("common.fileRequired") as string)}
        </p>
      )}
    </div>
  );
};

export default FileDropzone;
